import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCVGenerationStore } from '../../stores/cvGenerationStore'
import { api } from '../../lib/api'

interface HistoryRecord {
  id: string
  docx_available: boolean
  pdf_available: boolean
  export_failed: boolean
}

type DownloadKind = 'docx' | 'pdf'

type ExportStatus = 'pending' | 'ready' | 'failed'

// ~60s of polling (20 × 3s) before we stop and treat the export as failed, so a
// silently-stuck export becomes a retryable terminal state instead of an
// infinite spinner (C28).
const MAX_POLL_ATTEMPTS = 20
const POLL_INTERVAL_MS = 3000

export default function ExportStep() {
  const { approvalResult, reset } = useCVGenerationStore()
  const navigate = useNavigate()
  const [downloadingKind, setDownloadingKind] = useState<DownloadKind | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [retrying, setRetrying] = useState(false)
  const [retryingPdf, setRetryingPdf] = useState(false)
  const [exportStatus, setExportStatus] = useState<ExportStatus>(() => {
    if (approvalResult?.docx_available) return 'ready'
    if (approvalResult?.export_failed) return 'failed'
    return 'pending'
  })
  // PDF is converted server-side from the docx after approval, so it lags the docx.
  const [pdfStatus, setPdfStatus] = useState<ExportStatus>(() =>
    approvalResult?.pdf_available ? 'ready' : 'pending',
  )
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const attemptsRef = useRef(0)
  const pdfPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pdfAttemptsRef = useRef(0)

  // Poll for docx readiness only while pending. Stop on ready, on a reported
  // export failure, or after the attempt cap — never poll forever.
  useEffect(() => {
    if (exportStatus !== 'pending' || !approvalResult?.cv_record_id) return

    const stop = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }

    attemptsRef.current = 0
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get<{ data: { cv_records: HistoryRecord[] } }>('/api/v1/cv/history')
        const record = res.data.cv_records.find((r) => r.id === approvalResult.cv_record_id)
        if (record?.docx_available) {
          setExportStatus('ready')
          stop()
          return
        }
        if (record?.export_failed) {
          setExportStatus('failed')
          stop()
          return
        }
      } catch {
        /* transient — still counts toward the attempt cap below */
      }
      attemptsRef.current += 1
      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
        setExportStatus('failed')
        stop()
      }
    }, POLL_INTERVAL_MS)

    return stop
  }, [approvalResult?.cv_record_id, exportStatus])

  // Poll for the server-side PDF once the docx exists. Bounded like the docx poll:
  // if it doesn't appear within the window, surface a "Generate PDF" retry instead
  // of spinning forever.
  useEffect(() => {
    if (exportStatus !== 'ready' || pdfStatus !== 'pending' || !approvalResult?.cv_record_id) return

    const stop = () => {
      if (pdfPollRef.current) {
        clearInterval(pdfPollRef.current)
        pdfPollRef.current = null
      }
    }

    pdfAttemptsRef.current = 0
    pdfPollRef.current = setInterval(async () => {
      try {
        const res = await api.get<{ data: { cv_records: HistoryRecord[] } }>('/api/v1/cv/history')
        const record = res.data.cv_records.find((r) => r.id === approvalResult.cv_record_id)
        if (record?.pdf_available) {
          setPdfStatus('ready')
          stop()
          return
        }
      } catch {
        /* transient — still counts toward the attempt cap below */
      }
      pdfAttemptsRef.current += 1
      if (pdfAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
        setPdfStatus('failed')
        stop()
      }
    }, POLL_INTERVAL_MS)

    return stop
  }, [approvalResult?.cv_record_id, exportStatus, pdfStatus])

  async function handleDownload(kind: DownloadKind) {
    if (!approvalResult?.cv_record_id) return
    setDownloadingKind(kind)
    setDownloadError(null)
    try {
      const res = await api.get<{ data: { download_url: string; filename: string } }>(
        `/api/v1/cv/history/${approvalResult.cv_record_id}/download/${kind}`
      )
      const { download_url, filename } = res.data
      const blob = await fetch(download_url).then((r) => r.blob())
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = filename
      anchor.click()
      URL.revokeObjectURL(objectUrl)
    } catch (err) {
      setDownloadError((err as Error).message)
    } finally {
      setDownloadingKind(null)
    }
  }

  async function handleRetryExport() {
    if (!approvalResult?.cv_record_id) return
    setRetrying(true)
    setDownloadError(null)
    try {
      await api.post(`/api/v1/cv/history/${approvalResult.cv_record_id}/export/retry`, {})
      // The retry endpoint renders synchronously, so success means it's ready.
      setExportStatus('ready')
    } catch (err) {
      setExportStatus('failed')
      setDownloadError((err as Error).message)
    } finally {
      setRetrying(false)
    }
  }

  async function handleRetryPdf() {
    if (!approvalResult?.cv_record_id) return
    setRetryingPdf(true)
    setDownloadError(null)
    try {
      await api.post(`/api/v1/cv/history/${approvalResult.cv_record_id}/export/retry-pdf`, {})
      // The retry-pdf endpoint converts synchronously, so success means it's ready.
      setPdfStatus('ready')
    } catch (err) {
      setPdfStatus('failed')
      setDownloadError((err as Error).message)
    } finally {
      setRetryingPdf(false)
    }
  }

  return (
    <div className="max-w-lg mx-auto text-center space-y-8 py-12">
      {/* Success icon */}
      <div className="w-16 h-16 mx-auto bg-green-50 rounded-full flex items-center justify-center">
        <span className="material-symbols-outlined text-3xl text-green-600">
          check_circle
        </span>
      </div>

      <div>
        <h1 className="text-2xl font-['IBM_Plex_Serif'] font-semibold text-[#1a1c1a] tracking-tight">
          Your CV is ready
        </h1>
        <p className="text-sm text-[#545f6e] mt-2">
          Your CV has been approved and locked. It's now part of your permanent record.
        </p>
      </div>

      {/* Export state: ready → download, pending → spinner, failed → retry */}
      {exportStatus === 'ready' && (
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => handleDownload('docx')}
              disabled={downloadingKind !== null}
              className="px-6 py-3 text-sm font-medium text-white rounded-lg bg-gradient-to-br from-[#622800] to-[#833c0b] hover:from-[#833c0b] hover:to-[#622800] disabled:opacity-50 transition-all shadow-sm hover:shadow-md inline-flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-lg">description</span>
              {downloadingKind === 'docx' ? 'Preparing...' : 'Download DOCX'}
            </button>
            {pdfStatus === 'ready' && (
              <button
                onClick={() => handleDownload('pdf')}
                disabled={downloadingKind !== null}
                className="px-6 py-3 text-sm font-medium text-[#833c0b] rounded-lg border border-[#833c0b]/40 hover:bg-[#833c0b]/5 disabled:opacity-50 transition-all inline-flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-lg">picture_as_pdf</span>
                {downloadingKind === 'pdf' ? 'Preparing...' : 'Download PDF'}
              </button>
            )}
            {pdfStatus === 'failed' && (
              <button
                onClick={handleRetryPdf}
                disabled={retryingPdf}
                className="px-6 py-3 text-sm font-medium text-[#833c0b] rounded-lg border border-[#833c0b]/40 hover:bg-[#833c0b]/5 disabled:opacity-50 transition-all inline-flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-lg">picture_as_pdf</span>
                {retryingPdf ? 'Generating...' : 'Generate PDF'}
              </button>
            )}
          </div>
          {pdfStatus === 'pending' && (
            <p className="text-xs text-[#877369] inline-flex items-center gap-2 justify-center">
              <span className="animate-spin inline-block w-3 h-3 border-2 border-[#833c0b]/30 border-t-[#833c0b] rounded-full" />
              Generating PDF…
            </p>
          )}
          <p className="text-xs text-[#877369]">
            Download link expires in 1 hour
          </p>
        </div>
      )}

      {exportStatus === 'pending' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <span className="animate-spin inline-block w-4 h-4 border-2 border-amber-400/30 border-t-amber-600 rounded-full shrink-0" />
          <p className="text-sm text-amber-700">
            Your DOCX is being generated — this usually takes under a minute.
          </p>
        </div>
      )}

      {exportStatus === 'failed' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
          <p className="text-sm text-red-700">
            We couldn't generate your DOCX file. Your CV is safely saved — you can
            retry the download, or find it later in CV History.
          </p>
          <button
            onClick={handleRetryExport}
            disabled={retrying}
            className="px-6 py-2 text-sm font-medium text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-all inline-flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-lg">refresh</span>
            {retrying ? 'Retrying...' : 'Retry export'}
          </button>
        </div>
      )}

      {/* Download error (distinct from export failure) */}
      {downloadError && exportStatus === 'ready' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{downloadError}</p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-center gap-4 pt-4">
        <button
          onClick={() => { reset(); navigate('/cv/generate') }}
          className="text-sm text-[#545f6e] hover:text-[#833c0b] transition-colors underline underline-offset-2"
        >
          Generate another CV
        </button>
        <button
          onClick={() => navigate('/cv/history')}
          className="text-sm text-[#545f6e] hover:text-[#833c0b] transition-colors underline underline-offset-2"
        >
          CV History
        </button>
        <button
          onClick={() => navigate('/library')}
          className="text-sm text-[#545f6e] hover:text-[#833c0b] transition-colors underline underline-offset-2"
        >
          Back to Library
        </button>
      </div>
    </div>
  )
}
